import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridToolbarComponent } from '../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { IGridComponent } from '../../../core/models/grid/grid-component';
import { Subject, takeUntil, tap, filter, forkJoin, Observable } from 'rxjs';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { GridHelper } from '../../../core/models/grid/grid-helper';
import { GridToolbarOptions } from '../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { ServiceFulfillmentGridRow } from '../models/service-fulfillment-grid-row';
import { nameOf } from '../../../core/functions/string-helpers';
import { ServiceFulfillmentService } from '../services/service-fulfillment.service';
import { ApplicationGridService } from '../../shared/services/application-grid.service';
import { ApplicationActionCellRendererComponent } from '../../../shared/components/grid/cell-renderers/dashboard/application-action-cell-renderer/application-action-cell-renderer.component';
import { ApplicationIdCellRendererComponent } from '../../../shared/components/grid/cell-renderers/dashboard/application-id-cell-renderer/application-id-cell-renderer.component';
import { MultiSelectFilterComponent } from '../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import { ServiceSelection } from '../../../core/models/applications/service-selection';
import { GridFilterDataService } from '../../../core/services/grid-filter-data/grid-filter-data.service';
import { FilterOption } from '../../../core/models/grid/filter-option';
import { DashboardConstants } from '../../dashboard/constants/dashboard-constants';
import { ServiceOrderCellRendererComponent } from '../../../shared/components/grid/cell-renderers/service-order-cell-renderer/service-order-cell-renderer.component';
import { ServiceOrderCellRendererParams } from '../../../shared/components/grid/cell-renderers/service-order-cell-renderer/models/service-order-cell-renderer-params';
import { WorkflowStateLabels } from '../constants/workflow-state';
import { WorkflowStateCellRendererComponent } from '../../../shared/components/grid/cell-renderers/service-fulfillment/workflow-state-cell-renderer/workflow-state-cell-renderer.component';
import { HandedOverCellRendererComponent } from '../../../shared/components/grid/cell-renderers/service-fulfillment/handed-over-cell-renderer/handed-over-cell-renderer.component';
import { HandedOverCellRendererParams } from '../../../shared/components/grid/cell-renderers/service-fulfillment/handed-over-cell-renderer/models/handed-over-cell-renderer-params';
import { WorkflowStateCellRendererParams } from '../../../shared/components/grid/cell-renderers/service-fulfillment/workflow-state-cell-renderer/models/workflow-state-cell-renderer-params';
import { DropdownService } from '../../../core/services/resources/dropdown.service';
import { formatToDate } from '../../../core/functions/date-helpers';
import { DropdownOption } from '../../../core/models/dropdown-option';
import { AdvisorCellRendererComponent } from '../../../shared/components/grid/cell-renderers/service-fulfillment/advisor-cell-renderer/advisor-cell-renderer.component';
import { AdvisorCellRendererParams } from '../../../shared/components/grid/cell-renderers/service-fulfillment/advisor-cell-renderer/models/advisor-cell-renderer-params';
import { RouteConstants } from '../../../core/constants/routes';
import { ApplicationGridOptionsService } from '../../shared/services/application-grid-options/application-grid-options.service';
import { ApplicationStatusCellRendererComponent } from '../../../shared/components/grid/cell-renderers/application-status-cell-renderer/application-status-cell-renderer.component';
import { ApplicationStatusCellRendererParams } from '../../../shared/components/grid/cell-renderers/application-status-cell-renderer/models/application-status-cell-renderer-params';

@Component({
    selector: 'app-service-fulfillment',
    imports: [AgGridAngular, GridToolbarComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './service-fulfillment.component.html',
})
export class ServiceFulfillmentComponent implements IGridComponent, OnDestroy {
    private destroy$ = new Subject<void>();

    private readonly serviceFulfillmentService = inject(
        ServiceFulfillmentService,
    );
    private readonly filterDataService = inject(GridFilterDataService);
    private readonly dropdownService = inject(DropdownService);
    private readonly applicationGridOptionsService = inject(
        ApplicationGridOptionsService,
    );

    context: unknown;
    rowData: ServiceFulfillmentGridRow[] = [];
    gridApi: GridApi | undefined;

    servicesFilterOptions: FilterOption[] = [];
    statusFilterOptions: FilterOption[] =
        this.filterDataService.getServiceFulfillmentApplicationStatusFilterOptions();
    workflowStateFilterOptions: FilterOption[] =
        this.filterDataService.getWorkflowStateFilterOptions();
    handedOverFilterOptions: FilterOption[] =
        this.filterDataService.getHandedOverFilterOptions();
    advisorDropdownOptions: DropdownOption[] = [];

    columnDefs: ColDef[] = [];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsv: true,
        exportCsvOptions: {
            fileName: () => GridHelper.getExportFileName('Service_Fulfillment'),
            columnKeys: this.getColumnDefs()
                .filter(
                    (
                        col, // Export all columns except for Action column
                    ) =>
                        col.field !==
                        nameOf<ServiceFulfillmentGridRow>(
                            'isCoordinationAllowed',
                        ),
                )
                .map((col) => col.field as string),
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        ...this.applicationGridOptionsService.getAdvisorHighlightingOptions(),
    };

    onGridReady(params: GridReadyEvent<unknown>): void {
        this.gridApi = params.api;

        forkJoin([this.loadFilterData(), this.loadDropdownData()])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.loadGridData();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                headerName: 'Service',
                field: nameOf<ServiceFulfillmentGridRow>('applicationPurpose'),
                cellRenderer: ServiceOrderCellRendererComponent,
                cellRendererParams: (params: ServiceOrderCellRendererParams) =>
                    ({
                        service: ApplicationGridService.formatServicesColumn(
                            params.data,
                        ),
                        orderId: params.data.orderId,
                        returnRoute:
                            RouteConstants.ServiceFulfillment.getFullRoute(
                                'INDEX',
                            ),
                    }) as ServiceOrderCellRendererParams,
                valueFormatter: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
                valueGetter: (params) => {
                    return ApplicationGridService.getServiceValue(params.data);
                },
                maxWidth: 150,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.servicesFilterOptions.map((option) => ({
                        label: option.label,
                        value: option.label,
                        selected: option.selected,
                    })),
                },
                getQuickFilterText: (params) => {
                    return ApplicationGridService.formatServicesColumnWithOrder(
                        params.data,
                    );
                },
            },
            {
                headerName: 'Service Selection',
                field: nameOf<ServiceFulfillmentGridRow>(
                    'doItYourselfAutocoord',
                ),
                valueGetter: (params) => {
                    return ApplicationGridService.formatServiceSelectionColumn(
                        params.data,
                    );
                },
                minWidth: 160,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions:
                        ServiceSelection.getServiceSelectionLabels().map(
                            (label) => ({
                                label: label,
                                value: label,
                                selected: false,
                            }),
                        ),
                },
            },
            {
                headerName: 'App ID /\nPSCA ID',
                field: nameOf<ServiceFulfillmentGridRow>('salmonLogId'),
                minWidth: 160,
                filter: true,
                cellRenderer: ApplicationIdCellRendererComponent,
                cellRendererParams: {
                    returnRoute:
                        RouteConstants.ServiceFulfillment.getFullRoute('INDEX'),
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicationIdColumn(
                        params.data,
                    );
                },
            },
            {
                headerName: 'Call Sign /\nFile #',
                field: nameOf<ServiceFulfillmentGridRow>('callSign'),
                filter: true,
                cellStyle: {
                    whiteSpace: 'pre-line', // preserves \n characters
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatCallSignColumn(
                        params.data,
                    );
                },
                minWidth: 150,
            },
            {
                headerName: 'Applicant /\nTx Location',
                field: nameOf<ServiceFulfillmentGridRow>('licenseeEntityName'),
                filter: true,
                minWidth: 180,
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicantTransmissionLocationColumn(
                        params.data,
                    );
                },
            },
            {
                headerName: 'User /\nCompany',
                field: nameOf<ServiceFulfillmentGridRow>('organizationName'),
                filter: true,
                valueGetter: (params) => {
                    return ApplicationGridService.formatCustomerToUserThenCompany(
                        params.data,
                    );
                },
                minWidth: 160,
            },
            {
                headerName: 'Handed Over To EWA',
                field: nameOf<ServiceFulfillmentGridRow>('handedOverToEWAAt'),
                filter: true,
                minWidth: 160,
                ...GridHelper.defaultDateColDef,
                valueGetter: (params) => {
                    const date =
                        params.data.handedOverToEWAAt ?? params.data.createdAt;
                    return formatToDate(date);
                },
                filterParams: {
                    ...GridHelper.defaultDateTimeFilterParams,
                },
            },
            {
                headerName: 'Advisor',
                field: nameOf<ServiceFulfillmentGridRow>('advisorId'),
                cellRenderer: AdvisorCellRendererComponent,
                cellRendererParams: () =>
                    ({
                        dropdownOptions: this.advisorDropdownOptions,
                    }) as AdvisorCellRendererParams,
                valueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    const advisorLabel = this.advisorDropdownOptions?.find(
                        (option) => option.value === rowData.advisorId,
                    )?.label;
                    return advisorLabel ?? '';
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: (this.advisorDropdownOptions ?? []).map(
                        (option) => ({
                            label: option.label,
                            value: option.label,
                            selected: false,
                        }),
                    ),
                },
                ...GridHelper.disableCellFocusBorderColDef,
                minWidth: 200,
                maxWidth: 300,
                tooltipValueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    const advisorLabel = this.advisorDropdownOptions?.find(
                        (option) => option.value === rowData.advisorId,
                    )?.label;
                    return advisorLabel ?? '';
                },
            },
            {
                headerName: 'Status',
                field: nameOf<ServiceFulfillmentGridRow>('filingStatus'),
                cellRenderer: ApplicationStatusCellRendererComponent,
                cellRendererParams: (
                    params: ApplicationStatusCellRendererParams,
                ) =>
                    ({
                        status: params.data.filingStatus,
                        concurrenceDate: params.data.concurrenceDate,
                        releaseDate: params.data.releaseDate,
                    }) as ApplicationStatusCellRendererParams,
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.statusFilterOptions.map((status) => ({
                        label: status.label,
                        value: status.label,
                        selected: status.selected,
                    })),
                },
                filterValueGetter: (params) =>
                    ApplicationGridService.getStatusFilterValue(params.data),
            },
            {
                headerName: 'Action',
                field: nameOf<ServiceFulfillmentGridRow>(
                    'isCoordinationAllowed',
                ),
                cellRenderer: ApplicationActionCellRendererComponent,
                cellRendererParams: {
                    returnRoute:
                        RouteConstants.ServiceFulfillment.getFullRoute('INDEX'),
                },
                filter: false,
                sortable: false,
                maxWidth: 250,
                suppressSizeToFit: true,
            },
            {
                headerName: 'Application State',
                field: nameOf<ServiceFulfillmentGridRow>('workflowState'),
                cellRenderer: WorkflowStateCellRendererComponent,
                cellRendererParams: () =>
                    ({
                        dropdownOptions:
                            this.dropdownService.getWorkflowStateOptions(),
                    }) as WorkflowStateCellRendererParams,
                valueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    return rowData.workflowState != null
                        ? WorkflowStateLabels[rowData.workflowState]
                        : '';
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: (this.workflowStateFilterOptions ?? []).map(
                        (option) => ({
                            label: option.label,
                            value: option.value,
                            selected: option.selected,
                        }),
                    ),
                },
                ...GridHelper.disableCellFocusBorderColDef,
                minWidth: 160,
                maxWidth: 180,
            },
            {
                headerName: 'Handed Over',
                field: nameOf<ServiceFulfillmentGridRow>('isHandedOver'),
                cellRenderer: HandedOverCellRendererComponent,
                cellRendererParams: () =>
                    ({
                        dropdownOptions:
                            this.dropdownService.getHandedOverOptions(),
                        onHandedOver: () => this.loadGridData(),
                    }) as HandedOverCellRendererParams,
                valueGetter: (params) => {
                    const rowData = params.data as ServiceFulfillmentGridRow;
                    // Strings to match the filter values
                    return rowData.isHandedOver ? 'Yes' : 'No';
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: (this.handedOverFilterOptions ?? []).map(
                        (option) => ({
                            label: option.label,
                            value: option.value,
                            selected: option.selected,
                        }),
                    ),
                },
                ...GridHelper.disableCellFocusBorderColDef,
                maxWidth: 130,
            },
        ];
    }

    private loadGridData() {
        this.serviceFulfillmentService
            .getAll()
            .pipe(
                takeUntil(this.destroy$),
                tap((data: ServiceFulfillmentGridRow[]) => {
                    this.rowData = data;
                    this.columnDefs = this.getColumnDefs();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private loadFilterData(): Observable<FilterOption[]> {
        return this.filterDataService.getApplicationPurposeFilterOptions().pipe(
            tap((purposeOptions) => {
                this.servicesFilterOptions = [
                    ...purposeOptions,
                    DashboardConstants.FRN_REQUEST_FILTER_OPTION,
                ];
            }),
        );
    }

    private loadDropdownData(): Observable<DropdownOption[]> {
        return this.dropdownService.getAdvisorOptions().pipe(
            tap((options) => {
                this.advisorDropdownOptions = options;
            }),
        );
    }
}
